import { Button, Modal, Paper, Stack, Title } from "@mantine/core";
import { IError } from "@mctypes/global.types";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useState } from "react";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";
import { formatDate } from "../../admin/selectors";

const FlatErrorTable: React.FC<{ errors: IError[]; onDeleteError: (_id: string) => void }> = ({
  errors,
  onDeleteError,
}) => {
  const [error, setError] = useState<IError>(null);
  const errorsReversed = useMemo(() => {
    return errors.reverse();
  }, [errors]);

  const columns: ColDef[] = [
    {
      field: "_id",
      hide: true,
    },
    {
      field: "undefined1",
      headerName: "",
      cellRenderer: (params) => (
        <Button color="red" size="compact-sm" px={6} onClick={() => deleteError(params.data._id)}>
          <i className="fa fa-trash fa-fw" />
        </Button>
      ),
      width: 55,
      resizable: false,
      sortable: false,
      cellStyle: { padding: 0, textAlign: "center" },
    },
    {
      field: "undefined2",
      headerName: "",
      cellRenderer: (params) => (
        <Button color="mc-primary" size="compact-sm" px="xs" onClick={() => setError(params.data)}>
          Show
        </Button>
      ),
      width: 100,
      resizable: false,
      sortable: false,
    },
    {
      field: "createdAt",
      headerName: "Created",
      cellRenderer: (params) => formatDate(params.value),
      width: 140,
    },
    {
      field: "user.name",
      headerName: "User",
      width: 140,
    },
    {
      field: "routeMessage",
      minWidth: 250,
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
        wordBreak: "break-word",
      },
    },
    {
      field: "errorMessage",
      cellClass: "d-block",
      minWidth: 300,
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
        wordBreak: "break-word",
      },
    },
    {
      field: "requestParams.method",
      headerName: "Method",
      width: 60,
    },
    {
      field: "requestParams.originalUrl",
      cellClass: "d-block",
      headerName: "Url",
      minWidth: 300,
    },
  ];

  const toggle = () => {
    setError(null);
  };

  const deleteError = async (_id: string) => {
    toggle();
    onDeleteError(_id);
  };

  return (
    <Paper withBorder p="sm" radius="sm" bg="#fdfdff" style={{ height: "100%", overflow: "auto" }}>
      <Stack gap="xs">
        <Title order={4} fw={400}>
          {errorsReversed.length} Errors
        </Title>
        {errorsReversed.length === 0 && <p className="text-success">No errors</p>}
        {errorsReversed.length > 0 && (
          <div className="ag-theme-quartz h-100 w-100">
            <AgGridReact
              domLayout={"autoHeight"}
              rowData={errorsReversed || []}
              defaultColDef={{
                suppressHeaderMenuButton: true,
                headerClass: "px-2 py-1",
              }}
              rowHeight={50}
              columnDefs={columns}
              suppressClickEdit={true}
              animateRows={false}
              suppressContextMenu={true}
              localeText={AG_GRID_LOCALE_NOR}
              pagination={errorsReversed.length > 25}
              paginationPageSize={50}
            />
          </div>
        )}
        {error != null && (
          <Modal opened onClose={toggle} title="Error info" size="80%" styles={{ content: { maxWidth: "1600px" } }}>
            <Paper bg="dark.8" p="sm" radius="sm">
              <pre className="text-white">errorMessage: {error.errorMessage}</pre>
              <pre className="text-white">routeMessage: {error.routeMessage}</pre>
              <pre className="text-white">requestParams: {JSON.stringify(error.requestParams, null, 2)}</pre>
              <pre className="text-white">metadata: {JSON.stringify(error.metadata, null, 2)}</pre>
              <pre className="text-white">user: {JSON.stringify(error.user, null, 2)}</pre>
              <pre className="text-white">Stacktrace: {error.stackTrace}</pre>
            </Paper>
          </Modal>
        )}
      </Stack>
    </Paper>
  );
};

export default FlatErrorTable;
